<template>
<w-sliding-popup ref="slidingPopup" title="이벤트 해제 이력" width="950px" @open="open" @close="close">
  <div slot="content">
    <div class="title-h4 title-bar">이벤트 해제 정보</div>
    <el-input class="mr-b25" v-model="eventFormData.eventReleaseInfo" readonly resize="none" type="textarea" :rows="2"/>
    <div class="title-h4 title-bar">이벤트 정보</div>
    <el-input style="margin-bottom:15px;" v-model="eventFormData.eventMessage" readonly resize="none" type="textarea" :rows="2"/>
    <el-row class="form-list" :gutter="15">
      <el-col :span="12">
        <el-form label-position="left">
          <el-form-item label="대상" label-width="120px">{{eventFormData.objName}}</el-form-item>
          <el-form-item label="관리구분" label-width="120px">{{eventFormData.managetypeName}}</el-form-item>
          <el-form-item label="장애티켓 발생여부" label-width="120px">{{eventFormData.troubleStatus}}</el-form-item>
        </el-form>
      </el-col>
      <el-col :span="12">
        <el-form size="mini" label-position="left">
          <el-form-item label="등급" label-width="120px">{{eventFormData.eventlevelName}}</el-form-item>
          <el-form-item label="자원구분" label-width="120px">{{eventFormData.rsctypeName}}</el-form-item>
          <el-form-item label="발생시각" label-width="120px">{{eventFormData.checkDatetime}}</el-form-item>
        </el-form>
      </el-col>
    </el-row>
    <div class="mr-t25" v-if="releaseHistType === 'manual'">
      <div class="title-h4 title-bar">수동 해제 정보</div>
      <el-row class="form-list">
        <el-col :span="12" :gutter="15">
          <el-form label-position="left">
            <el-form-item label="해제자" label-width="120px">{{eventFormData.managerName}}</el-form-item>
          </el-form>
        </el-col>
        <el-col :span="12">
          <el-form size="mini" label-position="left">
            <el-form-item label="해제 IP" label-width="120px">{{eventFormData.releaseIpaddress}}</el-form-item>
          </el-form>
        </el-col>
      </el-row>
      <el-row>
        <el-form>
          <el-form-item>
            <el-input v-model="eventData.releaseMessage" readonly resize="none" type="textarea" :rows="3" class="ticket-input-class"></el-input>
          </el-form-item>
        </el-form>
      </el-row>
    </div>
    <div class="mr-t25" v-else-if="rscDatatypeId === 'INTEGER' || rscDatatypeId === 'DOUBLE'">
      <div class="title-h4 title-bar">자동 해제 정보</div>
      <el-row v-loading="isChartLoading">
        <highcharts class="chart" :options="options" ref="highchart" />
      </el-row>
    </div>
  </div>
</w-sliding-popup>
</template>
<script>
import isNil from 'lodash/isNil';
import isEqual from 'lodash/isEqual';
import isArray from 'lodash/isArray';
import trim from 'lodash/trim';
import maxBy from 'lodash/maxBy';
import minBy from 'lodash/minBy';
import moment from 'moment';
import EventHistAPI from '@/api/event/event-hist';
import { lineOptions } from '@/constants/chart-options';
import cloneDeep from 'lodash/cloneDeep';
import { EMPTY_TEXT, getValueAfterCheckEmpty } from '@/utils/string';
import { getDateToString } from '@/utils/date';
import { isEmpty, convertHex2Rgba } from '@/utils/basic';
import { isNumber } from '@/utils/number';

const GREATER_THAN_OR_EQUAL_TO_STRING = 'gt';
const LESS_THAN_OR_EQUAL_TO_STRING = 'lt';

export default {
  name: 'WEventReleaseHistPopup',
  props: {
    eventData: {
      type: Object,
      default: () => {},
    },
    releaseHistType: String,
  },
  data() {
    return {
      options: cloneDeep(lineOptions),
      eventFormData: {
        eventMessage: null,
        objName: null,
        eventlevelName: null,
        managetypeName: null,
        rsctypeName: null,
        troubleStatus: null,
        checkDatetime: null,
        eventReleaseInfo: null,
        releaseMessage: null,
        managerName: null,
        releaseIpaddress: null,
      },
      chartMinValue: 0,
      chartMaxValue: 0,
      startDatetime: null,
      endDatetime: null,
      isChartLoading: false,
    };
  },
  computed: {
    rscDatatypeId() {
      return isNil(this.eventData) ? null : this.eventData.rscDatatypeId;
    },
  },
  methods: {
    open() {
      this.loadData();
    },
    close() {
      this.options.series = null;
    },
    /**
     * 이벤트 데이터 폼 설정 및 성능 자원 추이 데이터를 로드한다.
     * @return {[type]} [description]
     */
    loadData() {
      const eventData = this.eventData;
      // 이벤트 정보 폼 설정
      this.eventFormData = {
        eventMessage: eventData.eventMessage,
        objName: eventData.targetInfo,
        eventlevelName: eventData.eventlevelName,
        managetypeName: eventData.managetypeName,
        rsctypeName: eventData.rsctypeName,
        troubleStatus: eventData.troubleStatusName,
        checkDatetime: this.getDateFormatter(null, null, eventData.checkDatetime),
        eventReleaseInfo: this.getEventReleaseInfo(eventData),
        releaseMessage: eventData.releaseMessage,
        managerName: isNil(eventData.releaseManagerName) ? getValueAfterCheckEmpty(eventData.releaseManagerId) : `${eventData.releaseManagerName} (${eventData.releaseManagerId})`,
        releaseIpaddress: eventData.releaseIpaddress,
      };

      // 자동 이벤트 해제일 경우 자원 데이터 타입에 따라 성능 자원 추이 그래프 제공
      if (isEqual(this.releaseHistType, 'auto') && (isEqual(this.eventData.rscDatatypeId, 'INTEGER') || isEqual(this.eventData.rscDatatypeId, 'DOUBLE'))) {
        if (!isNil(this.eventData.checkDatetime)) {
          const startDatetime = moment(this.eventData.checkDatetime).subtract(20, 'hours').toDate().getTime();
          const endDatetime = moment(this.eventData.checkDatetime).add(4, 'hours').toDate().getTime();

          this.startDatetime = startDatetime;
          this.endDatetime = endDatetime;
          const params = {
            hashkeyIdString: this.eventData.hashkeyIdString,
            startDatetime,
            endDatetime,
          };

          this.isChartLoading = true;
          EventHistAPI.getAutoReleaseHistInfo(params).then((response) => {
            if (response.data.success) {
              const lineSeries = response.data.data[0];

              const minChartData = minBy(lineSeries.data, (item) => item[1]);
              const maxChartData = maxBy(lineSeries.data, (item) => item[1]);
              this.chartMinValue = isEmpty(lineSeries.data) || isEmpty(minChartData) ? 0 : minChartData[1];
              this.chartMaxValue = isEmpty(lineSeries.data) || isEmpty(maxChartData) ? 0 : maxChartData[1];

              const areaSeries = this.getThresholdSeries(this.eventData.eventMessage);
              this.options = this.getChartOptions(this.options, lineSeries, areaSeries);
              this.options.chart.height = 250;
            }
          }).finally(() => {
            this.isChartLoading = false;
          });
        }
      }
    },
    /**
     * 이벤트 해제 정보를 반환한다.
     * @param  {[type]} row [description]
     * @return {[type]}     [description]
     */
    getEventReleaseInfo(row) {
      const releaseDatetime = moment(row.releaseDatetime).format('YYYY[년] MM[월] DD[일] HH[시] mm[분] ss[초]');
      const releaseType = this.releaseHistType === 'manual' ? '수동해제' : '자동해제';
      const eventReleaseInfo = `해당 이벤트는 ${releaseDatetime}에 ${releaseType} 되었습니다.`;

      return eventReleaseInfo;
    },
    getDateFormatter(row, column, value) {
      if (!isNil(value)) {
        return getDateToString(value);
      }
      return EMPTY_TEXT;
    },
    /**
     * 전달 받은 파라미터로 차트 옵션 객체를 생성해서 반환한다.
     * @author Ju
     * @param  {Object} baseOptions 기본 차트 옵션 객체
     * @param  {Object} linChartData  라인 차트 데이터 객체
     * @param  {Object} areaSeries  Area Series
     * @return {Object}             가공된 차트 옵션 객체
     */
    getChartOptions(baseOptions, lineSeries, areaSeries) {
      const chartOptions = cloneDeep(baseOptions);
      const series = [];

      // Area
      let min = this.minChartValue;
      let max = this.maxChartValue;
      if (isArray(areaSeries)) {
        for (let i = 0; i < areaSeries.length; i += 1) {
          if (areaSeries[i] !== null) {
            if (areaSeries[i].min < min) {
              min = areaSeries[i].min;
            } else if (areaSeries[i].max < min) {
              min = areaSeries[i].max;
            }

            if (areaSeries[i].max > max) {
              max = areaSeries[i].max;
            } else if (areaSeries[i].min > max) {
              max = areaSeries[i].min;
            }
            series.push(areaSeries[i]);
          }
        }
      } else if (areaSeries !== null) {
        if (areaSeries.min < min) {
          min = areaSeries.min;
        } else if (areaSeries.max < min) {
          min = areaSeries.max;
        }

        if (areaSeries.max > max) {
          max = areaSeries.max;
        } else if (areaSeries.min > max) {
          max = areaSeries.min;
        }
        series.push(areaSeries);
      }

      // Line
      series.push(lineSeries);

      chartOptions.series = series;
      chartOptions.yAxis.min = min;
      chartOptions.yAxis.max = max;

      return chartOptions;
    },
    /**
     * 이벤트 메시지로 임계치에 대한 area series를 생성해서 반환한다.
     * @author Ju
     * @return {Object | Array} series 객체 또는 series 배열
     */
    getThresholdSeries(eventMessage) {
      let areaSeries = null;
      // 이벤트 메시지에서 : 이후 데이터를 임계치 데이터로 보고 가공 처리한다.
      if (eventMessage.lastIndexOf(':') > 0) {
        const value = eventMessage.substring(eventMessage.lastIndexOf(':') + 1);

        // and/or가 있을 경우 최대 2개의 조건에 대한 series만 가공한다.
        if (value.indexOf('and') > 0) {
          /* and */
          const arr = value.split('and');
          if (!isEmpty(arr)) {
            if (arr.length === 1) {
              const inequation = this.getInequation(arr[0]);
              if (inequation !== null) {
                areaSeries = this.createThresholdSeriesBySingleInequation(inequation);
              }
            } else {
              const fistInequation = this.getInequation(arr[0]);
              const secondInequation = this.getInequation(arr[1]);

              if (!isEmpty(fistInequation) && !isEmpty(secondInequation)) {
                if (fistInequation.inequalitySign === LESS_THAN_OR_EQUAL_TO_STRING && secondInequation.inequalitySign === LESS_THAN_OR_EQUAL_TO_STRING) {
                  const inqualityValue = fistInequation.value < secondInequation.value ? fistInequation.value : secondInequation.value;
                  areaSeries = this.createThresholdSeries({
                    min: null,
                    max: inqualityValue,
                  });
                } else if (fistInequation.inequalitySign === LESS_THAN_OR_EQUAL_TO_STRING && secondInequation.inequalitySign === GREATER_THAN_OR_EQUAL_TO_STRING) {
                  if (secondInequation.value < fistInequation.value) {
                    areaSeries = this.createThresholdSeries({
                      min: secondInequation.value,
                      max: fistInequation.value,
                    });
                  }
                } else if (fistInequation.inequalitySign === GREATER_THAN_OR_EQUAL_TO_STRING && secondInequation.inequalitySign === LESS_THAN_OR_EQUAL_TO_STRING) {
                  if (fistInequation.value < secondInequation.value) {
                    areaSeries = this.createThresholdSeries({
                      min: secondInequation.value,
                      max: fistInequation.value,
                    });
                  }
                } else if (fistInequation.inequalitySign === GREATER_THAN_OR_EQUAL_TO_STRING && secondInequation.inequalitySign === GREATER_THAN_OR_EQUAL_TO_STRING) {
                  const inqualityValue = fistInequation.value > secondInequation.value ? fistInequation.value : secondInequation.value;
                  areaSeries = this.createThresholdSeries({
                    min: inqualityValue,
                    max: null,
                  });
                }
              } else if (!isEmpty(fistInequation)) {
                areaSeries = this.createThresholdSeriesBySingleInequation(fistInequation);
              } else if (!isEmpty(secondInequation)) {
                areaSeries = this.createThresholdSeriesBySingleInequation(secondInequation);
              }
            }
          }
        } else if (value.indexOf('or') > 0) {
          /* or */
          const arr = value.split('or');
          if (!isEmpty(arr)) {
            if (arr.length === 1) {
              const inequation = this.getInequation(arr[0]);
              if (inequation !== null) {
                areaSeries = this.createThresholdSeriesBySingleInequation(inequation);
              }
            } else {
              const inequation = this.getInequation(arr[0]);
              if (LESS_THAN_OR_EQUAL_TO_STRING === inequation.inequalitySign) {
                this.createThresholdSeriesBySingleInequation(inequation);
              } else {
                const fistInequation = this.getInequation(arr[0]);
                const secondInequation = this.getInequation(arr[1]);

                if (!isEmpty(fistInequation) && !isEmpty(secondInequation)) {
                  if (fistInequation.inequalitySign === LESS_THAN_OR_EQUAL_TO_STRING && secondInequation.inequalitySign === LESS_THAN_OR_EQUAL_TO_STRING) {
                    const inqualityValue = fistInequation.value > secondInequation.value ? fistInequation.value : secondInequation.value;
                    areaSeries = this.createThresholdSeries({
                      min: null,
                      max: inqualityValue,
                    });
                  } else if (fistInequation.inequalitySign === LESS_THAN_OR_EQUAL_TO_STRING && secondInequation.inequalitySign === GREATER_THAN_OR_EQUAL_TO_STRING) {
                    if (secondInequation.value < fistInequation.value) {
                      areaSeries = this.createThresholdSeries({
                        min: null,
                        max: null,
                      });
                    } else {
                      areaSeries = [];
                      const firstData = this.createThresholdSeries({
                        min: secondInequation.value,
                        max: null,
                      });
                      if (!isEmpty(firstData)) {
                        areaSeries.push(firstData);
                      }
                      const secondData = this.createThresholdSeries({
                        min: null,
                        max: fistInequation.value,
                      });
                      if (!isEmpty(secondData)) {
                        areaSeries.push(secondData);
                      }
                    }
                  } else if (fistInequation.inequalitySign === GREATER_THAN_OR_EQUAL_TO_STRING && secondInequation.inequalitySign === LESS_THAN_OR_EQUAL_TO_STRING) {
                    if (fistInequation.value < secondInequation.value) {
                      areaSeries = this.createThresholdSeries({
                        min: null,
                        max: null,
                      });
                    } else {
                      areaSeries = [];
                      const firstData = this.createThresholdSeries({
                        min: fistInequation.value,
                        max: null,
                      });
                      if (!isEmpty(firstData)) {
                        areaSeries.push(firstData);
                      }
                      const secondData = this.createThresholdSeries({
                        min: null,
                        max: secondInequation.value,
                      });
                      if (!isEmpty(secondData)) {
                        areaSeries.push(secondData);
                      }
                    }
                  } else if (fistInequation.inequalitySign === GREATER_THAN_OR_EQUAL_TO_STRING && secondInequation.inequalitySign === GREATER_THAN_OR_EQUAL_TO_STRING) {
                    const inqualityValue = fistInequation.value < secondInequation.value ? fistInequation.value : secondInequation.value;
                    areaSeries = this.createThresholdSeries({
                      min: inqualityValue,
                      max: null,
                    });
                  }
                } else if (!isEmpty(fistInequation)) {
                  areaSeries = this.createThresholdSeriesBySingleInequation(fistInequation);
                } else if (!isEmpty(secondInequation)) {
                  areaSeries = this.createThresholdSeriesBySingleInequation(secondInequation);
                }
              }
            }
          }
        } else {
          /* 단일 */
          const inequation = this.getInequation(value);
          if (inequation !== null) {
            areaSeries = this.createThresholdSeriesBySingleInequation(inequation);
          }
        }
      }
      return areaSeries;
    },
    /**
     * 문자열로된 부등식을 객체로 가공한다.
     * @author Ju
     * @param  {String} value 부등식 문자열
     * @return {Object}       부등식 객체 (value: 숫자값, inequalitySign: 부등호 (gt/lt))
     */
    getInequation(value) {
      let separator;
      let inequalitySign;
      let calcValue;

      if (value.indexOf('<=') > 0) {
        separator = '<=';
        inequalitySign = LESS_THAN_OR_EQUAL_TO_STRING;
        calcValue = 0;
      } else if (value.indexOf('<') > 0) {
        separator = '<';
        inequalitySign = LESS_THAN_OR_EQUAL_TO_STRING;
        calcValue = -1;
      } else if (value.indexOf('>=') > 0) {
        separator = '>=';
        inequalitySign = GREATER_THAN_OR_EQUAL_TO_STRING;
        calcValue = 0;
      } else if (value.indexOf('>') > 0) {
        separator = '>';
        inequalitySign = GREATER_THAN_OR_EQUAL_TO_STRING;
        calcValue = 1;
      }

      const arr = value.split(separator);
      if (arr.length === 2) {
        // 임계치에 대한 수치 값을 구한다.
        let compareValue = trim(arr[1]);
        const unit = this.eventData.rscUnit;
        if (compareValue.indexOf(unit) > 0) {
          compareValue = compareValue.split(unit)[0];
        }

        let numberValue;

        try {
          if (isNumber(compareValue)) {
            numberValue = parseFloat(compareValue);
          } else {
            /* eslint-disable no-new-func */
            numberValue = new Function(`return ${compareValue}`)();
            /* eslint-enable no-new-func */
          }
        } catch (e) {
          numberValue = null;
        }

        if (numberValue !== null) {
          // 수치 값과 부등호가 있는 경우에만 객체를 만들어 반환.
          return {
            value: numberValue + calcValue,
            inequalitySign,
          };
        }
      }

      return null;
    },
    /**
     * 단일 부등식 객체로 area series를 생성한다.
     * @author Ju
     * @param  {Object} inequation 부등식 객체 (value: 숫자값, inequalitySign: 부등호 (gt/lt))
     * @return {Object}            series 객체
     */
    createThresholdSeriesBySingleInequation(inequation) {
      if (LESS_THAN_OR_EQUAL_TO_STRING === inequation.inequalitySign) {
        return this.createThresholdSeries({
          min: inequation.value,
          max: null,
        });
      }
      return this.createThresholdSeries({
        min: null,
        max: inequation.value,
      });
    },
    /**
     * min/max 값 기준으로 area series를 생성한다.
     * @author Ju
     * @param  {Object} data  min/max 필드 값으로 가지는 객체
     * @return {Object}            series 객체
     */
    createThresholdSeries(data) {
      const chartMinValue = this.chartMinValue;
      const chartMaxValue = this.chartMaxValue;

      // 차트의 최소/최대 데이터 기준으로 min/max 값 설정
      let min;
      let max;
      if (data.min === null) {
        min = data.max !== null && chartMinValue > data.max ? data.max - 10 : chartMinValue;
      } else {
        min = data.min;
      }

      if (data.max === null) {
        max = data.max !== null && chartMaxValue < data.min ? data.min + 10 : chartMaxValue;
      } else {
        max = data.max;
      }

      // area series 생성
      if (min !== null && max !== null) {
        const chartData = [[this.startDatetime, min, max], [this.endDatetime, min, max]];
        const areaChartOptions = {
          name: '임계치',
          type: 'arearange',
          lineWidth: 0,
          linkedTo: ':previous',
          color: convertHex2Rgba(this.eventData.eventlevelColorBg, 70),
          fillOpacity: 0.3,
          zIndex: 0,
          marker: {
            enabled: false,
          },
        };
        areaChartOptions.data = chartData;
        areaChartOptions.min = min;
        areaChartOptions.max = max;

        return areaChartOptions;
      }

      return null;
    },
  },
};
</script>

<style>
.el-form-ticket-select-class .el-input {
  margin-top: 10px;
}
</style>

<style module>
.el-row-padding {
  padding: 10px 0px 10px 0px;
}
</style>



// WEBPACK FOOTER //
// src/views/event/event-hist/popup/EventReleaseHistPopup.vue